import { useMemo } from "react";
import {
  List,
  Datagrid,
  TextField,
  DateField,
  DateInput,
  SelectInput,
  ReferenceInput,
  AutocompleteInput,
  useListContext,
  ListContextProvider,
  FunctionField,
  Button,
  SimpleList
} from "react-admin";
import {
  EmployeeServicePath,
  STAFF_REQUEST,
  StaffRequestTypes,
  MOBILE_BREAKPOINT,
  LEAVE_DOCUMENTS_ATTACHABLE_STATUSES,
} from "utils/constants";
import { StaffRequestStatus } from "utils/choices";
import { getDisplayName } from "utils/helpers";
import { employeeRequests } from "api/employeeApi";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router";
import { EmptyList } from "server/components";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import { formatDays } from "../components/formatDays";
import { staffRequestStatusLabel } from "../components/staffRequestStatusLabel";
import AttachFilesDialog from "./AttachFilesDialog";

const filters = [
  <ReferenceInput
    source="employeeId"
    reference={employeeRequests.getEmployeesSelect()}
    filter={{ filterByUserNode: true }}
    alwaysOn
  >
    <AutocompleteInput
      label="Име"
      optionValue="id"
      optionText={getDisplayName}
      filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
      sx={{ minWidth: 280 }}
    />
  </ReferenceInput>,
  <SelectInput
    label="Статус"
    source="status"
    choices={StaffRequestStatus}
    emptyText="Всички"
    alwaysOn
  />,
  <DateInput
    label="От дата"
    source="startDate"
    alwaysOn
  />,
  <DateInput
    label="До дата"
    source="endDate"
    alwaysOn
  />,
];

const statusLabel = rec => staffRequestStatusLabel(rec?.status);

// leaves/node returns one flat row per leave — no `periods`, so the day count arrives as a plain
// number and has to be worded here (leaves/mine words it inside flattenLeaveRecord).
const daysLabel = rec => (rec?.numberOfDays == null ? "—" : formatDays(rec.numberOfDays));

const RowActions = ({ rec, navigate, iconOnly = false }) => (
  <Box
    sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", alignItems: "center", justifyItems: "center" }}
  >
    <Box>
      <Button
        label={iconOnly ? "" : "Детайли"}
        onClick={() =>
          navigate(`/${EmployeeServicePath}details/${STAFF_REQUEST}/${rec?.id}`, {
            state: { type: StaffRequestTypes.AnnualLeave, readOnly: true },
          })
        }
      >
        <VisibilityIcon />
      </Button>
    </Box>
    <Box>
      {LEAVE_DOCUMENTS_ATTACHABLE_STATUSES.includes(rec?.status) && (
        <SecuredComponent permission={permissions.canManageNodeLeaveDocuments}>
          <AttachFilesDialog
            leaveId={rec?.id}
            iconOnly={iconOnly}
          />
        </SecuredComponent>
      )}
    </Box>
  </Box>
);

const MobileView = ({ navigate }) => (
  <SimpleList
    empty={<EmptyList />}
    linkType={false}
    sx={settings.style.mobileList}
    primaryText={rec => (
      <div>
        <MobileListRow fields={[{ label: "№", value: rec.id }]} />
        <MobileListRow fields={[{ label: "Именa", value: rec?.employeeName || "—" }]} />
      </div>
    )}
    secondaryText={rec => (
      <div>
        <MobileListRow fields={[{ label: "Работни дни", value: daysLabel(rec) }]} />
        <MobileListRow fields={[{ label: "Статус", value: statusLabel(rec) }]} />
        <MobileListRow
          fields={[
            {
              label: "Дата на подаване",
              value: <DateField source="createdOn" locales="bg-BG" emptyText="—" />,
            },
          ]}
        />
        <MobileListRow fields={[{ label: "Основание", value: rec?.reason || "—" }]} />
      </div>
    )}
    tertiaryText={rec => (
      <RowActions
        rec={rec}
        navigate={navigate}
        iconOnly
      />
    )}
  />
);

const DesktopView = ({ navigate }) => (
  <Datagrid
    sx={{
      "& .RaDatagrid-headerCell": { textAlign: "left" },
      "& .RaDatagrid-headerCell:last-of-type, & .RaDatagrid-rowCell:last-of-type": { textAlign: "center" },
    }}
    bulkActionButtons={false}
    rowClick={false}
  >
    <TextField
      source="id"
      label="№"
    ></TextField>
    <TextField
      source="employeeName"
      label="Именa"
      emptyText="—"
      sortable={false}
    ></TextField>
    <DateField
      source="createdOn"
      label="Дата на подаване"
      locales="bg-BG"
      emptyText="—"
      sortable={false}
    ></DateField>
    <FunctionField
      label="Работни дни"
      sortable={false}
      render={daysLabel}
    />
    <TextField
      source="reason"
      label="Основание"
      emptyText="—"
      sortable={false}
    ></TextField>
    <FunctionField
      label="Статус"
      sortable={false}
      render={statusLabel}
    />
    <FunctionField
      label="Действия"
      sortable={false}
      render={rec => (
        <RowActions
          rec={rec}
          navigate={navigate}
        />
      )}
    />
  </Datagrid>
);

const ModifiedNodeLeavesDisplayData = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const listContext = useListContext();
  const { data, sort } = listContext;

  // The BE ignores SortBy/SortOrder for this endpoint and returns the rows id-DESC, so re-sort the
  // current page by № on the client. Copy first — `data` is the array react-query holds in its
  // cache and Array.prototype.sort mutates in place. Only this page is sorted; paging stays BE-ordered.
  const rows = useMemo(() => {
    if (!data || sort?.field !== "id") return data;
    return [...data].sort((a, b) => (sort.order === "ASC" ? a.id - b.id : b.id - a.id));
  }, [data, sort?.field, sort?.order]);

  return (
    <ListContextProvider value={{ ...listContext, data: rows }}>
      {isMobile ? <MobileView navigate={navigate} /> : <DesktopView navigate={navigate} />}
    </ListContextProvider>
  );
};

const NodeLeavesList = () => {
  return (
    <List
      title="Отпуски на звеното"
      filters={filters}
      exporter={false}
    >
      <ModifiedNodeLeavesDisplayData />
    </List>
  );
};
export default NodeLeavesList;
